import { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash-es/debounce';
import { useApplicationContext } from 'contexts/ApplicationContext';
import i18nContext from 'components/i18n-context';
import { PAYMENT_OBLIGATION_STATUS } from 'components/constants';
import CustomSearchInput from 'components/CustomSearchInput';
import Loader from 'components/Loader';
import Button from 'uikit/Button/Button';
import { CustomDatePicker } from 'uikit/CustomDatePicker';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';
import { InputDropDownSearch } from 'uikit/InputDropDown/InputDropDownSearch';
import './PaymentObligationsFiltersBar.scss';

const PaymentObligationsFiltersBar = ({
  isLoading,
  isInitialized,
  filters,
  totalElements,
  handleSetFilters,
  handleExportObligationList
}) => {
  const i18n = useContext(i18nContext);
  const [searchValue, setSearchValue] = useState(filters.search_text ? filters.search_text : '');
  const currentDate = new Date();
  const { constants } = useApplicationContext();

  const debouncedSearch = useRef(
    debounce((name, value) => {
      handleSetFilters(name, value);
    }, 400)
  ).current;

  const handleSearchTextInputChange = ({ target: { name, value } }) => {
    setSearchValue(value);

    debouncedSearch(name, value);
  };

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const paymentObligationsStatusesOptions = Object.keys(PAYMENT_OBLIGATION_STATUS).map((status) => {
    return {
      key: PAYMENT_OBLIGATION_STATUS[status],
      value: i18n.getMessage('paymentObligationStatus.' + PAYMENT_OBLIGATION_STATUS[status])
    };
  });

  const currencyOptions = constants?.currencies?.map((currency) => {
    return {
      key: currency,
      value: currency
    };
  });

  return (
    <div className={'payment-obligations-filters-wrapper'}>
      <div className={'payment-obligations-actions-wrapper'}>
        <div className={'payment-obligations-date-range-filters-wrapper'}>
          <div>
            <span>{i18n.getMessage('paymentObligations.filter.from.label')}</span>
            <CustomDatePicker
              onChange={handleSetFilters}
              name={'from_date'}
              value={filters.from_date}
              maxDate={filters.to_date || currentDate}
            />
          </div>
          <div>
            <span>{i18n.getMessage('paymentObligations.filter.to.label')}</span>
            <CustomDatePicker
              onChange={handleSetFilters}
              name={'to_date'}
              value={filters.to_date}
              maxDate={currentDate}
              minDate={filters.from_date}
            />
          </div>
        </div>
        <div className={'export-payment-obligation-report-action-wrapper'}>
          <Button
            className={'payment-obligations-button'}
            size={'medium'}
            type={'primary'}
            onClick={handleExportObligationList}
            isDisabled={!isInitialized || isLoading}
          >
            {isLoading ? <Loader /> : i18n.getMessage('paymentObligations.button.downloadCsv')}
          </Button>
        </div>
      </div>

      <InputDropDown
        className={'payment-obligations-filter-wrapper'}
        label={i18n.getMessage('paymentObligations.filter.status.label')}
        name={'statuses'}
        value={filters.statuses}
        options={paymentObligationsStatusesOptions}
        onChange={handleSetFilters}
        isMulti
      />
      <InputDropDownSearch
        className={'payment-obligations-filter-wrapper-input-dropdown-search'}
        id={'currencies'}
        name={'currencies'}
        label={i18n.getMessage('paymentObligations.filter.currency.label')}
        value={filters.currencies}
        options={currencyOptions}
        onChange={handleSetFilters}
        isDisabled={isLoading}
        isMulti
      />
      <CustomSearchInput
        wrapperClassName={'search-wrapper'}
        inputClassName={'payment-obligations-filter-wrapper'}
        label={i18n.getMessage('paymentObligations.filter.search.label')}
        placeholder={i18n.getMessage('paymentObligations.filter.search.placeholder')}
        name={'search_text'}
        value={searchValue}
        onChange={handleSearchTextInputChange}
        totalElements={totalElements}
      />
    </div>
  );
};

PaymentObligationsFiltersBar.propTypes = {
  isLoading: PropTypes.bool,
  isInitialized: PropTypes.bool,
  filters: PropTypes.object,
  totalElements: PropTypes.number,
  handleSetFilters: PropTypes.func,
  handleExportObligationList: PropTypes.func
};

export default PaymentObligationsFiltersBar;
